<script lang="ts" setup>
import { computed, reactive, Ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { AxiosResponse } from 'axios';

import { useVuelidate, Validation, ValidationArgs } from '@vuelidate/core';
import { required, maxLength } from '@/utils/validators';

import { useUiStore } from '@/stores';
import { goBack } from '@/composables/useNavigation';
import { IntegrationService } from '@/services';
import { toast, toastServiceError } from '@/services/core/notification';

import {
    FormAction,
    DefaultServiceResult,
    IntegrationServiceCreateResult,
    IntegrationType,
    PipedriveIntegrationModel,
    PipedriveIntegrationFormState,
    Permission,
} from '@/types';

const uiStore = useUiStore();
const route = useRoute();

const props = defineProps({
    action: {
        type: String,
        default: FormAction.CREATE,
    },
});

const formState = reactive<PipedriveIntegrationFormState>({
    type: IntegrationType.Pipedrive,
    token: null,
    checkActivityAsDone: true,
    associateWithDeal: true,
    isActive: true,
});

const rules = computed<ValidationArgs<PipedriveIntegrationFormState>>(() => ({
    type: { required },
    token: { required, maxLength: maxLength(250) },
    checkActivityAsDone: { required },
    associateWithDeal: { required },
    isActive: { required },
}));

const v$: Ref<Validation<ValidationArgs<PipedriveIntegrationFormState>>> = useVuelidate(
    rules,
    formState,
);

const setLoading = (loading: boolean) => uiStore.setIsLoading(loading);

const getIntegration = async (id: number) => {
    await setLoading(true);
    try {
        const { data } = await IntegrationService.get<PipedriveIntegrationModel>(id);
        Object.assign(formState, data);
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

const onSubmit = async () => {
    const isValid = await v$.value.$validate();
    if (!isValid) return;

    await setLoading(true);

    try {
        let response: AxiosResponse<DefaultServiceResult | IntegrationServiceCreateResult>;

        if (props.action === FormAction.EDIT) {
            const id = Number(route.params.id);
            response = await IntegrationService.update<DefaultServiceResult>(id, formState);
        } else {
            response = await IntegrationService.create<IntegrationServiceCreateResult>(formState);
        }

        toast.success(response.data.message);
        v$.value.$reset();
        goBack({ name: 'ListIntegration' });
    } catch (error) {
        toastServiceError(error);
    } finally {
        await setLoading(false);
    }
};

onMounted(() => {
    if (props.action === FormAction.EDIT) {
        getIntegration(Number(route.params.id));
    }
});
</script>

<template>
    <div class="space-y-4 p-4 sm:p-6 lg:p-8">
        <UiPageHeader>
            <template #info>
                <h1 class="text-xl font-semibold text-gray-800">
                    {{ $t('core.entity.Integrations') }} -
                    {{ $t('integration.integrations.pipedrive.title') }}
                </h1>
            </template>
        </UiPageHeader>
        <form class="space-y-4" autocomplete="off" @submit.prevent="onSubmit">
            <UiPanel>
                <div class="grid grid-cols-1 gap-4 lg:grid-cols-2">
                    <UiTextInput
                        v-model="v$.token.$model"
                        name="token"
                        type="text"
                        :label="$t('integration.integrations.pipedrive.form.token')"
                        :errors="v$.token.$errors"
                        @blur="v$.token.$touch"
                    />
                    <div class="flex-none items-end lg:flex">
                        <UiCheckboxInput
                            v-model="v$.checkActivityAsDone.$model"
                            name="checkActivityAsDone"
                            :label="
                                $t('integration.integrations.pipedrive.form.checkActivityAsDone')
                            "
                            class="mb-3 ml-0 mr-0 lg:ml-4 lg:mr-4"
                        />

                        <UiCheckboxInput
                            v-model="v$.associateWithDeal.$model"
                            name="associateWithDeal"
                            :label="$t('integration.integrations.pipedrive.form.associateWithDeal')"
                            class="mb-3 ml-0 lg:ml-4"
                        />
                    </div>
                </div>
                <div class="mt-6 w-full">
                    <UiCheckboxInput
                        v-model="v$.isActive.$model"
                        name="isActive"
                        :label="$t('integration.integrations.pipedrive.form.isActive')"
                    />
                </div>
            </UiPanel>

            <div class="flex justify-end px-4 sm:px-0">
                <UiBackButton :fallbackTo="{ name: 'ListIntegration' }" />
                <UiButton
                    v-if="$can(Permission.EDIT_INTEGRATION)"
                    type="submit"
                    variant="primary"
                    text-variant="white"
                    class="ml-4"
                >
                    {{ $t('core.actions.Save') }}
                </UiButton>
            </div>
        </form>
        <UiCheckDirtyBeforeRouteLeave :dirty="v$.$anyDirty" />
    </div>
</template>
